import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const testimonials = [
  {
    id: 1,
    name: 'Ahmet Yılmaz',
    role: 'İş İnsanı',
    comment: 'Havalimanı transferlerinde sürekli kullanıyorum. Zamanında geliyorlar, araçlar tertemiz. Profesyonel hizmet için teşekkürler.',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=professional%20turkish%20businessman%20portrait%20clean%20background%20confident%20smile%20formal%20attire%20studio%20lighting&width=100&height=100&seq=testimonial1&orientation=squarish',
  },
  {
    id: 2,
    name: 'Zeynep Kaya',
    role: 'Öğretmen',
    comment: 'Gece geç saatlerde bile güvenle kullanabiliyorum. Şoförler çok kibar ve yardımcı. Ailem için de tercih ediyorum.',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=professional%20turkish%20female%20teacher%20portrait%20warm%20smile%20clean%20background%20friendly%20appearance%20natural%20lighting&width=100&height=100&seq=testimonial2&orientation=squarish',
  },
  {
    id: 3,
    name: 'Mehmet Demir',
    role: 'Mühendis',
    comment: 'Çok hızlı ve güvenilir. Şoförler yolu çok iyi biliyor, trafiği akıllıca geçiyorlar. Kesinlikle tavsiye ederim.',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=professional%20turkish%20engineer%20portrait%20glasses%20clean%20background%20confident%20expression%20modern%20style&width=100&height=100&seq=testimonial3&orientation=squarish',
  },
  {
    id: 4,
    name: 'Ayşe Şahin',
    role: 'Doktor',
    comment: 'Hastaneye acil gitmem gerektiğinde hemen ulaşabildim. Çok hızlı ve güvenli bir hizmet sundular. Her zaman tercihim.',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=professional%20turkish%20female%20doctor%20portrait%20medical%20attire%20clean%20background%20trustworthy%20appearance%20soft%20lighting&width=100&height=100&seq=testimonial4&orientation=squarish',
  },
  {
    id: 5,
    name: 'Can Özkan',
    role: 'Girişimci',
    comment: 'Kurumsal anlaşmamız var, tüm ekip memnun. Fatura sistemi düzenli çalışıyor. Profesyonel ve güvenilir bir firma.',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=young%20turkish%20entrepreneur%20portrait%20modern%20casual%20business%20attire%20clean%20background%20energetic%20smile%20natural%20lighting&width=100&height=100&seq=testimonial5&orientation=squarish',
  },
  {
    id: 6,
    name: 'Fatma Arslan',
    role: 'Ev Hanımı',
    comment: 'Alışveriş dönüşlerinde düzenli kullanıyorum. Şoförler çok nazik, bagajlarımı taşımaya yardım ediyorlar. Çok memnunum.',
    rating: 5,
    image: 'https://readdy.ai/api/search-image?query=friendly%20turkish%20middle%20aged%20woman%20portrait%20warm%20smile%20clean%20background%20casual%20attire%20natural%20lighting%20pleasant&width=100&height=100&seq=testimonial6&orientation=squarish',
  },
];

export default function Testimonials() {
  const { ref: titleRef, inView: titleInView } = useInView(0.2);
  const { ref: gridRef, inView: gridInView } = useInView(0.05);

  return (
    <section id="yorumlar" className="py-24 bg-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />

      <div className="max-w-7xl mx-auto px-8">
        <div
          ref={titleRef}
          className="text-center mb-16"
          style={{
            opacity: titleInView ? 1 : 0,
            transform: titleInView ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.9s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <div className="inline-flex items-center space-x-2 bg-yellow-400 px-6 py-3 rounded-full mb-6 shadow-lg">
            <i className="ri-star-fill text-black text-lg" />
            <span className="text-black text-xs font-black uppercase tracking-widest whitespace-nowrap">Müşteri Yorumları</span>
          </div>
          <h2 className="text-6xl font-black text-black leading-tight mb-4">
            Müşterilerimiz<br />Ne Diyor?
          </h2>
          <div
            className="h-1 bg-yellow-400 mx-auto mb-4"
            style={{
              width: titleInView ? '96px' : '0px',
              transition: 'width 1s ease 0.4s',
            }}
          />
          <p className="text-gray-600 text-base max-w-md mx-auto">
            Binlerce memnun müşterimizin deneyimlerini okuyun
          </p>
        </div>

        <div ref={gridRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, i) => (
            <div
              key={testimonial.id}
              className="bg-zinc-950 rounded-2xl p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 relative overflow-hidden group border-l-4 border-transparent hover:border-yellow-400"
              style={{
                opacity: gridInView ? 1 : 0,
                transform: gridInView ? 'translateY(0)' : 'translateY(50px)',
                transition: `all 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 0.12}s`,
              }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-400/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700" />

              <div className="relative z-10">
                <div className="flex space-x-1 mb-5">
                  {[...Array(testimonial.rating)].map((_, idx) => (
                    <i
                      key={idx}
                      className="ri-star-fill text-yellow-400 text-lg"
                      style={{
                        opacity: gridInView ? 1 : 0,
                        transform: gridInView ? 'scale(1)' : 'scale(0)',
                        transition: `all 0.4s ease ${i * 0.12 + idx * 0.06}s`,
                      }}
                    />
                  ))}
                </div>

                <i className="ri-double-quotes-l text-5xl text-yellow-400 mb-4 block opacity-30" />

                <p className="text-gray-300 leading-relaxed mb-8 text-sm min-h-[80px]">{testimonial.comment}</p>

                <div className="flex items-center space-x-4 border-t border-white/10 pt-6">
                  <div className="relative">
                    <img
                      src={testimonial.image}
                      alt={`${testimonial.name} - Başharık Taksi Malatya Müşteri Yorumu`}
                      className="w-14 h-14 rounded-full object-cover border-2 border-yellow-400 shadow-lg"
                    />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-black text-xs" />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-black text-white text-base whitespace-nowrap">{testimonial.name}</h4>
                    <p className="text-xs text-gray-500 whitespace-nowrap">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
    </section>
  );
}
